import { useMemo } from 'react';

import { useDateTimeFormat } from '../../date-time-picker/hooks/useDateTimeFormat.js';
import type { DateTimeInputProps } from '../types.js';

/**
 * Hook that provides a regex to match the start of a date for the current locale.
 */
export const useDateTimeParser = ({
  precision,
  type = 'datetime',
}: Pick<DateTimeInputProps, 'precision' | 'type'>) => {
  const { emptyParts } = useDateTimeFormat(type, precision);

  const { maxValue, regexDateTimeStart, types } = useMemo(() => {
    const types = emptyParts.map(({ type }) => type);

    // max allowed numeric value for date time part type.
    const maxValue = types[0] === 'day' ? 31 : types[0] === 'month' ? 12 : 9999;

    // max nr of digits for first datetime part
    const maxDigits = Math.ceil(Math.log10(maxValue));

    // delimiter that divides first and second datetime part
    const delimiter =
      emptyParts.find(({ type }) => type === 'literal')?.value ?? '.';

    const regexDateTimeStart = new RegExp(
      `^(\\d{1,${maxDigits}})${delimiter.trim().replaceAll('.', '[.]')}$`,
    );

    return {
      maxValue,
      regexDateTimeStart,
      types,
    };
  }, [emptyParts]);

  return {
    maxValue,
    regexDateTimeStart,
    types,
  };
};
